
import React, { useState, useMemo } from 'react';
import { Search, Syringe, AlertTriangle, Calculator, Info, Activity, Droplets, Beaker, Zap, AlertOctagon, HeartPulse, Pill, Thermometer } from 'lucide-react';
import { VETERINARY_DRUGS } from '../constants';


// --- Types & Constants for CRI ---

type Species = 'Canine' | 'Feline';
type WeightUnit = 'kg' | 'lb';
type CalculationMode = 'syringe' | 'fluid'; // Syringe Pump vs Fluid Additive

interface CRIDrugDefinition {
  id: string;
  name: string;
  category: 'Analgesic' | 'Anesthetic' | 'Emergency' | 'Multimodal';
  doseUnit: 'mcg/kg/min' | 'mcg/kg/hr' | 'mg/kg/hr' | 'mg/kg/day' | 'Units/kg/hr';
  defaultDose: number;
  safeRange: string;
  defaultConcentration: number; // mg/ml or Units/ml
  toxicToCats: boolean;
  isCocktail?: boolean; // For MLK/FLK
  components?: string[]; // Description of cocktail
}

const CRI_DRUGS: CRIDrugDefinition[] = [
  // Multimodal
  { id: 'mlk', name: 'MLK (Morphine/Lido/Ket)', category: 'Multimodal', doseUnit: 'mg/kg/hr', defaultDose: 0, safeRange: 'Based on LIDO rate', defaultConcentration: 0, toxicToCats: true, isCocktail: true, components: ['Morphine', 'Lidocaine', 'Ketamine'] },
  { id: 'flk', name: 'FLK (Fentanyl/Lido/Ket)', category: 'Multimodal', doseUnit: 'mcg/kg/hr', defaultDose: 0, safeRange: 'Based on LIDO rate', defaultConcentration: 0, toxicToCats: true, isCocktail: true, components: ['Fentanyl', 'Lidocaine', 'Ketamine'] },
  { id: 'mk', name: 'MK (Morphine/Ketamine)', category: 'Multimodal', doseUnit: 'mg/kg/hr', defaultDose: 0, safeRange: 'Cat Safe', defaultConcentration: 0, toxicToCats: false, isCocktail: true, components: ['Morphine', 'Ketamine'] },
  
  // Analgesics
  { id: 'fentanyl', name: 'Fentanyl', category: 'Analgesic', doseUnit: 'mcg/kg/hr', defaultDose: 3, safeRange: '1 - 5 mcg/kg/hr', defaultConcentration: 0.05, toxicToCats: false },
  { id: 'morphine', name: 'Morphine', category: 'Analgesic', doseUnit: 'mg/kg/hr', defaultDose: 0.1, safeRange: '0.1 - 0.5 mg/kg/hr', defaultConcentration: 15, toxicToCats: false },
  { id: 'hydro', name: 'Hydromorphone', category: 'Analgesic', doseUnit: 'mg/kg/hr', defaultDose: 0.05, safeRange: '0.01 - 0.1 mg/kg/hr', defaultConcentration: 2, toxicToCats: false },
  { id: 'ketamine', name: 'Ketamine (Pain)', category: 'Analgesic', doseUnit: 'mcg/kg/min', defaultDose: 10, safeRange: '2 - 20 mcg/kg/min', defaultConcentration: 100, toxicToCats: false },
  { id: 'lidocaine', name: 'Lidocaine 2%', category: 'Analgesic', doseUnit: 'mcg/kg/min', defaultDose: 25, safeRange: '25 - 50 mcg/kg/min', defaultConcentration: 20, toxicToCats: true },
  { id: 'dex', name: 'Dexmedetomidine', category: 'Analgesic', doseUnit: 'mcg/kg/hr', defaultDose: 1, safeRange: '0.5 - 3 mcg/kg/hr', defaultConcentration: 0.5, toxicToCats: false },
  
  // Anesthetic
  { id: 'propofol', name: 'Propofol', category: 'Anesthetic', doseUnit: 'mcg/kg/min', defaultDose: 100, safeRange: '50 - 200 mcg/kg/min', defaultConcentration: 10, toxicToCats: false },
  { id: 'alfaxalone', name: 'Alfaxalone', category: 'Anesthetic', doseUnit: 'mg/kg/hr', defaultDose: 2, safeRange: '1 - 5 mg/kg/hr', defaultConcentration: 10, toxicToCats: false },
  { id: 'midazolam', name: 'Midazolam', category: 'Anesthetic', doseUnit: 'mg/kg/hr', defaultDose: 0.2, safeRange: '0.1 - 0.5 mg/kg/hr', defaultConcentration: 5, toxicToCats: false },
  
  // Emergency
  { id: 'insulin', name: 'Insulin (Regular)', category: 'Emergency', doseUnit: 'Units/kg/hr', defaultDose: 0.1, safeRange: '0.05 - 0.2 U/kg/hr', defaultConcentration: 100, toxicToCats: false }, 
  { id: 'metoclopramide', name: 'Metoclopramide', category: 'Emergency', doseUnit: 'mg/kg/day', defaultDose: 1, safeRange: '1 - 2 mg/kg/day', defaultConcentration: 5, toxicToCats: false },
  { id: 'dobutamine', name: 'Dobutamine', category: 'Emergency', doseUnit: 'mcg/kg/min', defaultDose: 5, safeRange: '2 - 15 mcg/kg/min', defaultConcentration: 12.5, toxicToCats: false },
  { id: 'dopamine', name: 'Dopamine', category: 'Emergency', doseUnit: 'mcg/kg/min', defaultDose: 5, safeRange: '2 - 10 mcg/kg/min', defaultConcentration: 40, toxicToCats: false },
  { id: 'norepi', name: 'Norepinephrine', category: 'Emergency', doseUnit: 'mcg/kg/min', defaultDose: 0.1, safeRange: '0.05 - 1.0 mcg/kg/min', defaultConcentration: 1, toxicToCats: false },
];

const FORMULARY_CATEGORIES = [
    { id: 'All', label: 'All Drugs', icon: Pill },
    { id: 'Antibiotic', label: 'Antibiotics', icon: Activity },
    { id: 'Analgesic', label: 'Pain Mgmt', icon: HeartPulse },
    { id: 'Anesthetic', label: 'Anesthesia', icon: Syringe },
    { id: 'Sedative', label: 'Sedatives', icon: Syringe },
    { id: 'Emergency', label: 'Emergency', icon: Zap },
    { id: 'Gastrointestinal', label: 'GI / Anti-Emetic', icon: Thermometer },
    { id: 'Steroid', label: 'Steroids', icon: Pill },
];

export const DrugCalculator: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'formulary' | 'cri'>('formulary');
  
  // --- Formulary State ---
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [formWeight, setFormWeight] = useState<number | ''>(10);
  const [formWeightUnit, setFormWeightUnit] = useState<WeightUnit>('kg');
  const drugs = VETERINARY_DRUGS;
  const [selectedDrugId, setSelectedDrugId] = useState<string | null>(null);
  
  // --- CRI State ---
  const [criSpecies, setCriSpecies] = useState<Species>('Canine');
  const [criWeight, setCriWeight] = useState<number | ''>(20);
  const [criWeightUnit, setCriWeightUnit] = useState<WeightUnit>('kg');
  const [criMode, setCriMode] = useState<CalculationMode>('syringe');
  const [selectedCriId, setSelectedCriId] = useState<string>('fentanyl');
  
  // Inputs
  const [doseInput, setDoseInput] = useState<number | ''>('');
  const [concInput, setConcInput] = useState<number | ''>('');
  const [fluidRateInput, setFluidRateInput] = useState<number | ''>(10); // ml/hr
  const [bagSizeInput, setBagSizeInput] = useState<number | ''>(250); // ml

  // --- Logic ---

  const selectedDrug = useMemo(() => drugs.find(d => d.id === selectedDrugId) || null, [drugs, selectedDrugId]);
  
  const filteredDrugs = useMemo(() => {
      const term = searchTerm.toLowerCase();
      return drugs.filter(d => {
          const matchesTerm = 
            d.name.toLowerCase().includes(term) || 
            d.category.toLowerCase().includes(term) ||
            (d.indications && d.indications.toLowerCase().includes(term));
          const matchesCategory = activeCategory === 'All' || d.category.includes(activeCategory);
          return matchesTerm && matchesCategory;
      }).sort((a,b) => a.name.localeCompare(b.name));
  }, [searchTerm, drugs, activeCategory]);
  
  const selectedCRI = useMemo(() => CRI_DRUGS.find(d => d.id === selectedCriId) || CRI_DRUGS[3], [selectedCriId]);
  
  useMemo(() => {
    if (selectedCRI) {
        setDoseInput(selectedCRI.defaultDose);
        setConcInput(selectedCRI.defaultConcentration);
    }
  }, [selectedCRI]);

  const weightInKg = (w: number | '', unit: WeightUnit) => {
    if (typeof w !== 'number') return 0;
    return unit === 'kg' ? w : w / 2.20462;
  };

  const calculateCRI = () => {
    const W = weightInKg(criWeight, criWeightUnit);
    const Dose = typeof doseInput === 'number' ? doseInput : 0;
    const Conc = typeof concInput === 'number' ? concInput : 0;
    const Rate = typeof fluidRateInput === 'number' ? fluidRateInput : 0;
    const Bag = typeof bagSizeInput === 'number' ? bagSizeInput : 0;

    if (W === 0 || Dose === 0 || Conc === 0) return { result: 0, label: '---' };

    let unitFactor = 1;
    if (selectedCRI.doseUnit === 'mcg/kg/min') unitFactor = 0.06; 
    else if (selectedCRI.doseUnit === 'mcg/kg/hr') unitFactor = 0.001;
    else if (selectedCRI.doseUnit === 'mg/kg/day') unitFactor = 1 / 24;
    
    const mgPerHour = Dose * W * unitFactor;

    if (criMode === 'syringe') {
        const rate = mgPerHour / Conc;
        return { result: rate, label: 'mL/hr' };
    } else {
        if (Rate === 0) return { result: 0, label: '---' };
        const hoursLasts = Bag / Rate;
        const totalMgNeeded = mgPerHour * hoursLasts;
        const volToAdd = totalMgNeeded / Conc;
        return { result: volToAdd, label: 'mL to add to bag' };
    }
  };

  const calcResult = calculateCRI();
  const handleFormularyWeight = (w: number | '') => setFormWeight(w);

  const renderFormulary = () => (
    <div className="h-full flex flex-col md:flex-row gap-6 p-6 overflow-hidden relative">
      {/* Left: List */}
      <div className="w-full md:w-1/3 flex flex-col bg-slate-900 rounded-3xl shadow-lg border border-white/5 overflow-hidden">
        <div className="p-5 border-b border-white/5 bg-slate-900/50">
             <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold text-white flex items-center gap-2 brand-font"><Search className="w-5 h-5 text-indigo-400" /> Formulary</h2>
             </div>
             <div className="relative mb-3">
                 <input type="text" placeholder="Search drugs, indications..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-white/10 bg-slate-950/50 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all text-sm text-white placeholder-slate-500" />
                 <Search className="w-4 h-4 text-slate-500 absolute left-3 top-3.5" />
             </div>
             
             {/* Category Filters */}
             <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
                 {FORMULARY_CATEGORIES.map(cat => {
                     const Icon = cat.icon;
                     const isActive = activeCategory === cat.id;
                     return (
                         <button
                            key={cat.id}
                            onClick={() => setActiveCategory(cat.id)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-colors border ${
                                isActive 
                                ? 'bg-indigo-600 text-white border-indigo-600' 
                                : 'bg-slate-800 text-slate-400 border-white/5 hover:border-white/10 hover:bg-slate-700'
                            }`}
                         >
                             <Icon className="w-3 h-3" />
                             {cat.label}
                         </button>
                     )
                 })}
             </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-2 custom-scrollbar">
            {filteredDrugs.length === 0 ? (
                <div className="text-center py-8 text-slate-500 text-sm">No drugs found matching criteria.</div>
            ) : (
                filteredDrugs.map(drug => (
                    <button key={drug.id} onClick={() => setSelectedDrugId(drug.id)} className={`w-full text-left p-3 rounded-xl transition-all border ${selectedDrugId === drug.id ? 'bg-indigo-900/20 border-indigo-500/30 shadow-inner' : 'bg-slate-900/50 border-transparent hover:bg-slate-800 hover:border-white/5'}`}>
                        <div className="flex justify-between items-start">
                            <div>
                                <span className={`font-bold block ${selectedDrugId === drug.id ? 'text-white' : 'text-slate-300'}`}>{drug.name}</span>
                                <span className="text-xs text-slate-500 font-medium">{drug.category}</span>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                                {drug.concentration > 0 && <span className="text-[10px] bg-slate-800 text-slate-400 px-2 py-0.5 rounded-full font-bold border border-white/5">{drug.concentration} mg/ml</span>}
                                {drug.species && drug.species !== 'Both' && (
                                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${drug.species === 'Canine' ? 'bg-indigo-900/30 text-indigo-300 border-indigo-500/20' : 'bg-purple-900/30 text-purple-300 border-purple-500/20'}`}>
                                        {drug.species}
                                    </span>
                                )}
                            </div>
                        </div>
                    </button>
                ))
            )}
        </div>
      </div>
      {/* Right: Details */}
      <div className="w-full md:w-2/3 flex flex-col bg-slate-900 rounded-3xl shadow-lg border border-white/5 overflow-hidden">
         <div className="p-6 border-b border-white/5 bg-slate-900/50 flex justify-between items-center">
            <h2 className="text-lg font-bold text-white flex items-center gap-2 brand-font"><Calculator className="w-5 h-5 text-indigo-400" /> Dose Calc</h2>
            <div className="flex items-center gap-4">
                <div className="flex items-center bg-slate-950 rounded-xl border border-white/10 p-1 focus-within:ring-2 focus-within:ring-indigo-500/50">
                    <input type="number" value={formWeight} onChange={(e) => handleFormularyWeight(parseFloat(e.target.value) || '')} className="w-20 px-2 py-1 outline-none text-right font-mono text-white bg-transparent text-lg font-bold" placeholder="0" />
                    <div className="flex bg-slate-900 rounded-lg ml-2 overflow-hidden border border-white/5">
                        <button onClick={() => setFormWeightUnit('kg')} className={`px-3 py-1 text-xs font-bold transition-colors ${formWeightUnit === 'kg' ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:text-slate-300'}`}>kg</button>
                        <button onClick={() => setFormWeightUnit('lb')} className={`px-3 py-1 text-xs font-bold transition-colors ${formWeightUnit === 'lb' ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:text-slate-300'}`}>lb</button>
                    </div>
                </div>
            </div>
         </div>
         {selectedDrug ? (
             <div className="p-8 flex-1 overflow-y-auto custom-scrollbar">
                 <div className="mb-8 p-6 bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl border border-white/5 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 relative overflow-hidden">
                    <div className="absolute inset-0 bg-indigo-500/5"></div>
                    <div className="relative z-10">
                        <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-3xl font-bold text-white">{selectedDrug.name}</h3>
                            {selectedDrug.species === 'Canine' && <span className="bg-indigo-900/50 text-indigo-300 text-xs px-2 py-1 rounded font-bold uppercase border border-indigo-500/30">Dog Only</span>}
                            {selectedDrug.species === 'Feline' && <span className="bg-purple-900/50 text-purple-300 text-xs px-2 py-1 rounded font-bold uppercase border border-purple-500/30">Cat Only</span>}
                        </div>
                        <p className="text-indigo-400 font-bold text-sm uppercase tracking-wide">{selectedDrug.category}</p>
                    </div>
                    <div className="text-right w-full md:w-auto p-4 bg-slate-950/50 rounded-xl border border-white/5 backdrop-blur-sm relative z-10">
                        {selectedDrug.concentration > 0 ? (
                            <>
                                <div className="text-sm text-slate-400 font-bold uppercase tracking-wide mb-1">Calculated Dose</div>
                                <div className="text-5xl font-bold text-white font-mono tracking-tighter">
                                    {((weightInKg(formWeight, formWeightUnit) * selectedDrug.defaultDosage) / selectedDrug.concentration).toFixed(2)} <span className="text-xl text-slate-500">mL</span>
                                </div>
                                <p className="text-xs text-slate-500 mt-1 font-bold">
                                    Total: {(weightInKg(formWeight, formWeightUnit) * selectedDrug.defaultDosage).toFixed(1)} mg
                                </p>
                            </>
                        ) : <div className="text-2xl text-slate-500 font-mono font-bold">Oral/Solid</div>}
                    </div>
                 </div>

                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                     <div className="space-y-4">
                         <div className="p-4 bg-slate-950/50 rounded-xl border border-white/5">
                             <label className="text-[10px] uppercase text-slate-500 font-bold block mb-1">Concentration</label>
                             <p className="font-mono text-xl text-slate-300 font-bold">{selectedDrug.concentration > 0 ? `${selectedDrug.concentration} mg/ml` : 'Varies (Tablet)'}</p>
                         </div>
                         <div className="p-4 bg-slate-950/50 rounded-xl border border-white/5">
                             <label className="text-[10px] uppercase text-slate-500 font-bold block mb-1">Standard Dosage</label>
                             <p className="font-mono text-xl text-slate-300 font-bold">{selectedDrug.defaultDosage} mg/kg</p>
                             {selectedDrug.doseRange && <p className="text-xs text-slate-400 mt-1 font-medium bg-slate-900 px-2 py-1 rounded inline-block border border-white/5">{selectedDrug.doseRange}</p>}
                         </div>
                     </div>
                     <div className="space-y-4">
                        <div className="p-5 bg-amber-900/10 rounded-xl border border-amber-500/20 h-full">
                            <div className="flex items-center gap-2 mb-3 text-amber-500 font-bold uppercase text-xs tracking-wide">
                                <AlertTriangle className="w-4 h-4" /> Contraindications
                            </div>
                            {selectedDrug.contraindications.length > 0 ? (
                                <ul className="list-disc list-inside text-sm text-amber-200/80 font-medium space-y-1">
                                    {selectedDrug.contraindications.map((c, i) => <li key={i}>{c}</li>)}
                                </ul>
                            ) : (
                                <span className="text-sm text-amber-500/40 italic">None listed.</span>
                            )}
                        </div>
                     </div>
                 </div>

                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div className="bg-slate-950/50 p-5 rounded-xl border border-white/5">
                        <label className="flex items-center gap-2 text-xs font-bold uppercase text-slate-500 mb-3"><Activity className="w-4 h-4" /> Indications</label>
                        <p className="text-sm font-medium text-slate-300 leading-relaxed">{selectedDrug.indications || 'General usage.'}</p>
                    </div>
                    <div className="bg-slate-950/50 p-5 rounded-xl border border-white/5">
                        <label className="flex items-center gap-2 text-xs font-bold uppercase text-slate-500 mb-3"><AlertOctagon className="w-4 h-4" /> Side Effects</label>
                        <p className="text-sm font-medium text-slate-300 leading-relaxed">{selectedDrug.sideEffects || 'None common listed.'}</p>
                    </div>
                 </div>

                 <div>
                     <label className="flex items-center gap-2 text-xs font-bold uppercase text-slate-500 mb-3"><Info className="w-4 h-4" /> Clinical Notes</label>
                     <p className="text-slate-400 leading-relaxed bg-slate-950/30 p-5 rounded-xl border border-white/5 text-sm font-medium text-justify">
                         {selectedDrug.notes}
                     </p>
                 </div>
             </div>
         ) : <div className="flex-1 flex flex-col items-center justify-center text-slate-600"><Syringe className="w-20 h-20 mb-6 opacity-20" /><p className="text-lg font-bold text-slate-500">Select a drug to view details.</p></div>}
      </div>
    </div>
  );

  const renderCRI = () => (
    <div className="h-full flex flex-col xl:flex-row gap-6 p-6 overflow-hidden bg-slate-950 text-slate-200">
        {/* Left: Controls */}
        <div className="w-full xl:w-5/12 bg-slate-900 rounded-3xl shadow-lg border border-white/5 overflow-y-auto custom-scrollbar">
            <div className="p-5 border-b border-white/5 bg-slate-900/50">
                <h2 className="text-lg font-bold text-white flex items-center gap-2 brand-font">
                    <Activity className="w-5 h-5 text-indigo-400" /> CRI Parameters
                </h2>
                <div className="mt-4 flex gap-2">
                    <button onClick={() => setCriSpecies('Canine')} className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all border ${criSpecies === 'Canine' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-slate-800 text-slate-400 border-white/5 hover:bg-slate-700'}`}>Dog</button>
                    <button onClick={() => setCriSpecies('Feline')} className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all border ${criSpecies === 'Feline' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-slate-800 text-slate-400 border-white/5 hover:bg-slate-700'}`}>Cat</button>
                </div>
            </div>
            
            <div className="p-6 space-y-6">
                {/* Weight Input */}
                <div className="bg-slate-950/50 p-4 rounded-2xl border border-white/5">
                    <div className="flex justify-between items-center mb-2">
                        <label className="text-xs font-bold text-slate-500 uppercase">Patient Weight</label>
                    </div>
                    <div className="flex items-center bg-slate-900 rounded-xl border border-white/10 p-1 focus-within:ring-2 focus-within:ring-indigo-500/50">
                        <input type="number" value={criWeight} onChange={(e) => setCriWeight(parseFloat(e.target.value) || '')} className="w-full px-3 py-2 outline-none text-right font-mono text-xl text-white bg-transparent font-bold" placeholder="0" />
                        <div className="flex bg-slate-800 rounded-lg ml-2 overflow-hidden border border-white/5">
                            <button onClick={() => setCriWeightUnit('kg')} className={`px-3 py-2 text-xs font-bold transition-colors ${criWeightUnit === 'kg' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-slate-200'}`}>kg</button>
                            <button onClick={() => setCriWeightUnit('lb')} className={`px-3 py-2 text-xs font-bold transition-colors ${criWeightUnit === 'lb' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-slate-200'}`}>lb</button>
                        </div>
                    </div>
                </div>

                {/* Mode Toggle */}
                <div className="grid grid-cols-2 gap-4">
                    <button onClick={() => setCriMode('syringe')} className={`p-4 rounded-2xl border text-left transition-all relative overflow-hidden group shadow-sm ${criMode === 'syringe' ? 'bg-indigo-900/20 border-indigo-500/30' : 'bg-slate-900 border-white/5 hover:border-white/10'}`}>
                        <Syringe className={`w-6 h-6 mb-2 ${criMode === 'syringe' ? 'text-indigo-400' : 'text-slate-500'}`} />
                        <div className={`font-bold ${criMode === 'syringe' ? 'text-indigo-300' : 'text-slate-400'}`}>Syringe Pump</div>
                        <div className="text-[10px] text-slate-500 uppercase tracking-wide mt-1 font-bold">Output: mL/hr</div>
                    </button>
                    <button onClick={() => setCriMode('fluid')} className={`p-4 rounded-2xl border text-left transition-all relative overflow-hidden group shadow-sm ${criMode === 'fluid' ? 'bg-teal-900/20 border-teal-500/30' : 'bg-slate-900 border-white/5 hover:border-white/10'}`}>
                        <Droplets className={`w-6 h-6 mb-2 ${criMode === 'fluid' ? 'text-teal-400' : 'text-slate-500'}`} />
                        <div className={`font-bold ${criMode === 'fluid' ? 'text-teal-300' : 'text-slate-400'}`}>Fluid Additive</div>
                        <div className="text-[10px] text-slate-500 uppercase tracking-wide mt-1 font-bold">Output: mL to Bag</div>
                    </button>
                </div>

                {/* Drug Selection */}
                <div>
                    <label className="text-xs font-bold text-slate-500 uppercase mb-2 block">Select Protocol / Drug</label>
                    <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto custom-scrollbar pr-2">
                        {CRI_DRUGS.map(d => (
                            <button 
                                key={d.id} 
                                onClick={() => setSelectedCriId(d.id)}
                                disabled={criSpecies === 'Feline' && d.toxicToCats}
                                className={`w-full text-left p-3 rounded-xl border flex items-center justify-between transition-all ${
                                    selectedCriId === d.id 
                                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-md' 
                                    : (criSpecies === 'Feline' && d.toxicToCats) 
                                        ? 'bg-slate-950/50 border-red-900/30 opacity-50 cursor-not-allowed text-slate-600'
                                        : 'bg-slate-900 border-white/5 text-slate-400 hover:bg-slate-800 hover:border-white/10'
                                }`}
                            >
                                <div>
                                    <div className="font-bold text-sm">{d.name}</div>
                                    <div className={`text-[10px] ${selectedCriId === d.id ? 'text-indigo-200' : 'text-slate-500'}`}>{d.category}</div>
                                </div>
                                {(criSpecies === 'Feline' && d.toxicToCats) && <AlertOctagon className="w-4 h-4 text-red-500" />}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>

        {/* Right: Calculator & Results */}
        <div className="w-full xl:w-7/12 flex flex-col gap-6 overflow-y-auto custom-scrollbar">
            {selectedCRI.isCocktail ? (
                // --- MULTIMODAL RECIPE CARD ---
                <div className="bg-slate-900 rounded-3xl shadow-lg border border-white/5 p-8 flex flex-col items-center justify-center text-center h-full">
                    <Beaker className="w-20 h-20 text-slate-700 mb-6" />
                    <h2 className="text-3xl font-bold text-white mb-2">{selectedCRI.name}</h2>
                    <p className="text-slate-400 max-w-md mb-8">This is a multimodal cocktail. Dosing is typically calculated based on a fixed rate or ratio.</p>
                    
                    {selectedCRI.id === 'mlk' && criSpecies === 'Canine' && (
                        <div className="bg-slate-950 p-6 rounded-2xl border border-white/10 text-left w-full max-w-lg">
                            <h3 className="text-indigo-400 font-bold uppercase text-xs tracking-widest mb-4">Standard Canine MLK Recipe (Bag)</h3>
                            <ul className="space-y-4 font-mono text-sm text-slate-400">
                                <li className="flex justify-between border-b border-white/5 pb-2"><span>Fluid Bag:</span> <span className="text-white font-bold">1000 mL (LRS/NaCl)</span></li>
                                <li className="flex justify-between border-b border-white/5 pb-2"><span>Morphine (15mg/ml):</span> <span className="text-white font-bold">Add 4.0 mL (60mg)</span></li>
                                <li className="flex justify-between border-b border-white/5 pb-2"><span>Lidocaine 2% (20mg/ml):</span> <span className="text-white font-bold">Add 50 mL (1000mg)</span></li>
                                <li className="flex justify-between pb-2"><span>Ketamine (100mg/ml):</span> <span className="text-white font-bold">Add 0.6 mL (60mg)</span></li>
                            </ul>
                            <div className="mt-4 p-3 bg-indigo-900/20 rounded-xl text-xs text-indigo-300 border border-indigo-500/20 font-medium">
                                Run at <strong>1-2 ml/kg/hr</strong> for intra-op pain management.
                            </div>
                        </div>
                    )}
                    {selectedCRI.id === 'mk' && (
                        <div className="bg-slate-950 p-6 rounded-2xl border border-white/10 text-left w-full max-w-lg">
                            <h3 className="text-teal-400 font-bold uppercase text-xs tracking-widest mb-4">Standard MK Recipe (Cat Safe)</h3>
                            <ul className="space-y-4 font-mono text-sm text-slate-400">
                                <li className="flex justify-between border-b border-white/5 pb-2"><span>Fluid Bag:</span> <span className="text-white font-bold">500 mL (LRS)</span></li>
                                <li className="flex justify-between border-b border-white/5 pb-2"><span>Morphine (15mg/ml):</span> <span className="text-white font-bold">Add 3.0 mL (45mg)</span></li>
                                <li className="flex justify-between pb-2"><span>Ketamine (100mg/ml):</span> <span className="text-white font-bold">Add 0.3 mL (30mg)</span></li>
                            </ul>
                            <div className="mt-4 p-3 bg-teal-900/20 rounded-xl text-xs text-teal-300 border border-teal-500/20 font-medium">
                                Run at <strong>2-3 ml/kg/hr</strong>.
                            </div>
                        </div>
                    )}
                    {criSpecies === 'Feline' && selectedCRI.toxicToCats && (
                        <div className="p-6 bg-red-900/20 border border-red-500/20 rounded-2xl flex items-center gap-4 text-red-300">
                            <AlertOctagon className="w-10 h-10 text-red-500" />
                            <div className="text-left">
                                <div className="font-bold text-lg">CONTRAINDICATED</div>
                                <div className="text-sm opacity-80">Lidocaine is toxic to cats. Do not use MLK/FLK. Use MK instead.</div>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                // --- SINGLE DRUG CALCULATOR ---
                <>
                    <div className="bg-slate-900 rounded-3xl shadow-lg border border-white/5 p-6">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-12 h-12 rounded-xl bg-indigo-600 flex items-center justify-center shadow-lg">
                                <Zap className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-white">{selectedCRI.name}</h2>
                                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500">
                                    <span>Safe Range:</span>
                                    <span className="text-emerald-400 bg-emerald-900/20 px-2 py-0.5 rounded border border-emerald-500/20">{selectedCRI.safeRange}</span>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div>
                                    <label className="text-[10px] font-bold text-slate-500 uppercase mb-1.5">Target Dose ({selectedCRI.doseUnit})</label>
                                    <input type="number" className="w-full p-3 rounded-xl bg-slate-950 border border-white/10 focus:ring-2 focus:ring-indigo-500 outline-none text-white font-mono text-lg font-bold" 
                                        value={doseInput} onChange={e => setDoseInput(parseFloat(e.target.value) || '')} />
                                </div>
                                <div>
                                    <label className="text-[10px] font-bold text-slate-500 uppercase mb-1.5">Drug Concentration (mg/ml or U/ml)</label>
                                    <input type="number" className="w-full p-3 rounded-xl bg-slate-950 border border-white/10 focus:ring-2 focus:ring-indigo-500 outline-none text-white font-mono text-lg font-bold" 
                                        value={concInput} onChange={e => setConcInput(parseFloat(e.target.value) || '')} />
                                </div>
                            </div>

                            {criMode === 'fluid' && (
                                <div className="space-y-4 p-4 bg-slate-950/50 rounded-2xl border border-white/5">
                                    <div>
                                        <label className="text-[10px] font-bold text-teal-400 uppercase mb-1.5">Fluid Bag Volume (ml)</label>
                                        <input type="number" className="w-full p-2 rounded-lg bg-slate-900 border border-white/10 text-white font-mono font-bold" 
                                            value={bagSizeInput} onChange={e => setBagSizeInput(parseFloat(e.target.value) || '')} />
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-bold text-teal-400 uppercase mb-1.5">Fluid Rate (ml/hr)</label>
                                        <input type="number" className="w-full p-2 rounded-lg bg-slate-900 border border-white/10 text-white font-mono font-bold" 
                                            value={fluidRateInput} onChange={e => setFluidRateInput(parseFloat(e.target.value) || '')} />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* RESULTS BOX */}
                    <div className="flex-1 bg-gradient-to-br from-indigo-900/30 to-blue-900/30 rounded-3xl border border-indigo-500/20 p-8 flex flex-col items-center justify-center text-center shadow-inner relative overflow-hidden group">
                        <div className="relative z-10">
                            <div className="text-sm font-bold text-indigo-300 uppercase tracking-widest mb-4">
                                {criMode === 'syringe' ? 'Set Syringe Driver To' : 'Add to Fluid Bag'}
                            </div>
                            <div className="text-7xl font-black text-white font-mono tracking-tighter mb-2 drop-shadow-lg">
                                {typeof calcResult.result === 'number' ? calcResult.result.toFixed(2) : '---'}
                            </div>
                            <div className="text-xl font-medium text-slate-400">{calcResult.label}</div>
                        </div>

                        {/* Disclaimer */}
                        <div className="absolute bottom-4 left-0 right-0 text-[10px] text-slate-600 text-center px-8">
                            <span className="font-bold text-amber-600">⚠ DISCLAIMER:</span> Educational use only. All doses must be verified by a licensed veterinarian.
                        </div>
                    </div>
                </>
            )}
        </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col overflow-hidden bg-slate-950">
        <div className="flex bg-slate-900 border-b border-white/5 px-6 pt-4 space-x-6">
            <button onClick={() => setActiveTab('formulary')} className={`pb-3 font-bold text-sm uppercase tracking-wide transition-colors border-b-4 ${activeTab === 'formulary' ? 'border-indigo-500 text-indigo-400' : 'border-transparent text-slate-500 hover:text-slate-300'}`}>Formulary</button>
            <button onClick={() => setActiveTab('cri')} className={`pb-3 font-bold text-sm uppercase tracking-wide transition-colors border-b-4 ${activeTab === 'cri' ? 'border-indigo-500 text-indigo-400' : 'border-transparent text-slate-500 hover:text-slate-300'}`}>CRI Calculator</button>
        </div>
        <div className="flex-1 overflow-hidden bg-slate-950">
            {activeTab === 'formulary' ? renderFormulary() : renderCRI()}
        </div>
    </div>
  );
};
